<template>
  <div id="app-root" class="flex flex-col h-screen antialiased" style="background:var(--gray-50);color:var(--gray-900)">

        <!-- Login View -->
        <div v-if="!token" class="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
            <div class="mx-auto flex min-h-screen w-full max-w-6xl items-stretch p-6 md:p-10">
                <div class="grid w-full grid-cols-1 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-2xl shadow-slate-950/50 md:grid-cols-2">
                    <div class="relative flex flex-col justify-between px-8 py-10 text-white">
                        <div>
                            <div class="flex items-center gap-3">
                                <div v-if="branding.headerLogoUrl" class="rounded-xl bg-white p-2">
                                    <img :src="branding.headerLogoUrl" alt="logo" class="h-9 w-auto" />
                                </div>
                                <div v-else class="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-2xl">📧</div>
                                <div class="leading-tight">
                                    <div class="text-sm font-semibold text-white/70">AI Mail Console</div>
                                    <div class="text-xl font-extrabold tracking-tight">{{ branding.headerTitle || 'AI 郵件伺服器' }}</div>
                                    <div class="text-xs text-white/50 mt-0.5">震亞物聯網</div>
                                </div>
                            </div>

                            <div class="mt-8">
                                <div class="text-3xl font-extrabold tracking-tight">{{ branding.loginTitle || '歡迎回來' }}</div>
                                <div class="mt-2 text-sm leading-relaxed text-white/70">
                                    以管理員權限登入後可管理網域、使用者、寄信稽核、AI 功能與信箱。
                                </div>
                            </div>

                            <div class="mt-8 grid gap-3 text-sm text-white/80">
                                <div class="flex items-start gap-3">
                                    <div class="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400/20 text-emerald-200">✓</div>
                                    <div>支援超級管理員 / 網域管理員 / 一般使用者權限</div>
                                </div>
                                <div class="flex items-start gap-3">
                                    <div class="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-sky-400/20 text-sky-200">✓</div>
                                    <div>內建 Webmail、日誌監控、寄信審核與報表</div>
                                </div>
                                <div class="flex items-start gap-3">
                                    <div class="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-violet-400/20 text-violet-200">✓</div>
                                    <div>整合 AI 分析、RAG 知識庫與行銷工具</div>
                                </div>
                            </div>
                        </div>

                        <div class="mt-10 text-xs text-white/50">
                            建議使用 Chrome / Edge 以獲得最佳體驗
                        </div>
                    </div>

                    <div class="flex items-center justify-center px-6 py-10 md:px-10" style="background:var(--surface);">
                        <div class="w-full max-w-sm">
                            <div class="mb-6">
                                <div class="text-xl font-extrabold" style="color:var(--gray-900);">登入</div>
                                <div class="mt-1 text-sm" style="color:var(--gray-500);">請輸入管理帳號與密碼</div>
                            </div>

                            <!-- Normal login form -->
                            <form v-if="!showTotpModal" @submit.prevent="login" class="space-y-4">
                                <div>
                                    <label class="block text-xs font-bold mb-2" style="color:var(--gray-600);">帳號 (Email)</label>
                                    <input v-model="loginForm.username" type="email" class="input" required placeholder="請輸入帳號（輸入 @ 可選網域）" autocomplete="username" list="login-domain-list">
                                    <datalist id="login-domain-list">
                                        <option v-for="s in loginEmailSuggestions" :key="s" :value="s"></option>
                                    </datalist>
                                    <p v-if="loginDomainHint" class="mt-1 text-xs" style="color:#b45309;">{{ loginDomainHint }}</p>
                                </div>
                                <div>
                                    <label class="block text-xs font-bold mb-2" style="color:var(--gray-600);">密碼</label>
                                    <input v-model="loginForm.password" type="password" class="input" required placeholder="請輸入密碼" autocomplete="current-password">
                                </div>
                                <button type="submit" class="btn btn-sm btn-primary w-full">
                                    登入
                                </button>
                                <p v-if="loginError" class="px-3 py-2 text-xs font-semibold"
                                    style="border:1px solid #fca5a5;background:var(--color-danger-bg);color:#991b1b;border-radius:var(--radius-lg);">
                                    {{ loginError }}
                                </p>
                            </form>

                            <!-- TOTP second-factor form -->
                            <form v-else @submit.prevent="totpLogin" class="space-y-4">
                                <div class="flex items-center gap-2 mb-2">
                                    <div class="flex h-9 w-9 items-center justify-center rounded-full text-lg font-bold" style="background:var(--brand-100);color:var(--brand-600);">🔐</div>
                                    <div>
                                        <div class="text-sm font-bold" style="color:var(--gray-800);">雙因素驗證</div>
                                        <div class="text-xs" style="color:var(--gray-500);">請輸入驗證器顯示的 6 位數代碼</div>
                                    </div>
                                </div>
                                <div>
                                    <label class="block text-xs font-bold mb-2" style="color:var(--gray-600);">驗證碼</label>
                                    <input
                                        v-model="totpCode"
                                        type="text"
                                        class="input text-center text-2xl font-mono tracking-widest"
                                        maxlength="8"
                                        placeholder="000000"
                                        autofocus
                                        autocomplete="one-time-code"
                                        inputmode="numeric"
                                        style="letter-spacing:0.25em;"
                                    >
                                    <div class="text-xs mt-1" style="color:var(--gray-400);">也可輸入備用碼（格式：XXXXXXXX）</div>
                                </div>
                                <button type="submit" class="btn btn-sm btn-primary w-full" :disabled="totpLoading">
                                    {{ totpLoading ? '驗證中…' : '確認' }}
                                </button>
                                <p v-if="totpError" class="px-3 py-2 text-xs font-semibold"
                                    style="border:1px solid #fca5a5;background:var(--color-danger-bg);color:#991b1b;border-radius:var(--radius-lg);">
                                    {{ totpError }}
                                </p>
                                <button type="button" class="text-xs underline w-full text-center" style="color:var(--gray-400);" @click="showTotpModal=false;totpPartialToken=''">
                                    返回登入頁
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="token && !currentUser" class="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
            <div class="mx-auto flex min-h-screen w-full max-w-lg items-center justify-center p-6">
                <div class="w-full rounded-2xl border border-white/10 bg-white/5 p-8 text-white shadow-2xl shadow-slate-950/50">
                    <div class="text-xl font-extrabold text-center">驗證登入中...</div>
                    <div class="mt-2 text-sm text-white/70 text-center">正在取得使用者資訊，請稍候。</div>
                    <div class="mt-6 flex justify-center">
                        <span class="animate-spin text-2xl">⟳</span>
                    </div>
                    <div class="mt-8">
                        <button type="button" class="btn btn-sm w-full" style="background:rgba(255,255,255,0.12);color:#fff;border:1px solid rgba(255,255,255,0.25);" @click="logout">重新登入</button>
                    </div>
                </div>
            </div>
        </div>


    <!-- Main Interface -->
    <div v-else class="app-layout">

      <!-- Mobile sidebar overlay (tap outside to close) -->
      <div class="sidebar-overlay" :class="{ 'sidebar-open': mobileSidebarOpen }" @click="closeMobileSidebar()"></div>

      <!-- Sidebar -->
      <AppSidebar />

      <!-- RIGHT SIDE (topbar + content) — hidden for full-screen views -->
      <div class="app-right" v-show="currentView !== 'webmail' && currentView !== 'unreplied' && currentView !== 'ai_sales'" style="flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0;">
        <!-- Topbar -->
        <AppTopbar />

                <!-- Command Palette -->
                <div id="cmd-overlay" v-if="showCmdPalette" @click.self="closeCmdPalette()" @keydown.stop>
                    <div id="cmd-box">
                        <div id="cmd-input-wrap">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:var(--gray-400);flex-shrink:0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input id="cmd-input" v-model="cmdQuery" @input="updateCmdResults()" @keydown.up.prevent="moveCmdSel(-1)" @keydown.down.prevent="moveCmdSel(1)" @keydown.enter.prevent="execCmdItem()" @keydown.esc="closeCmdPalette()" placeholder="搜尋功能、使用者、聯絡人…" autocomplete="off" />
                            <span id="cmd-shortcut-hint">ESC 關閉</span>
                        </div>
                        <div id="cmd-results">
                            <div v-if="cmdResults.length === 0 && cmdQuery.length > 0" style="padding:2rem;text-align:center;color:var(--gray-400);font-size:0.875rem;">
                                找不到「{{ cmdQuery }}」的相關結果
                            </div>
                            <template v-for="(group, gi) in cmdGrouped" :key="gi">
                                <div class="cmd-section-label">{{ group.label }}</div>
                                <div v-for="(item, ii) in group.items" :key="item.id"
                                    class="cmd-item"
                                    :class="{ 'cmd-active': cmdSelIdx === item._globalIdx }"
                                    @click="execCmdItemById(item)"
                                    @mouseenter="cmdSelIdx = item._globalIdx">
                                    <div class="cmd-item-icon" :class="item.iconClass">
                                        <span v-html="item.icon"></span>
                                    </div>
                                    <div class="cmd-item-main">
                                        <div class="cmd-item-title" v-html="item.titleHtml || item.title"></div>
                                        <div v-if="item.sub" class="cmd-item-sub">{{ item.sub }}</div>
                                    </div>
                                    <div v-if="item.badge" class="cmd-item-badge">{{ item.badge }}</div>
                                </div>
                            </template>
                            <!-- Default suggestions when no query -->
                            <template v-if="cmdQuery.length === 0">
                                <div class="cmd-section-label">常用功能</div>
                                <div v-for="item in cmdDefaults" :key="item.id"
                                    class="cmd-item"
                                    :class="{ 'cmd-active': cmdSelIdx === item._globalIdx }"
                                    @click="execCmdItemById(item)"
                                    @mouseenter="cmdSelIdx = item._globalIdx">
                                    <div class="cmd-item-icon" :class="item.iconClass"><span v-html="item.icon"></span></div>
                                    <div class="cmd-item-main">
                                        <div class="cmd-item-title">{{ item.title }}</div>
                                        <div v-if="item.sub" class="cmd-item-sub">{{ item.sub }}</div>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div id="cmd-footer">
                            <span><span class="cmd-key">↑</span><span class="cmd-key">↓</span> 選擇</span>
                            <span><span class="cmd-key">Enter</span> 執行</span>
                            <span><span class="cmd-key">Esc</span> 關閉</span>
                        </div>
                    </div>
                </div>


        <!-- Main Content -->
        <main class="app-main">
          <component :is="currentViewComponent" />
        </main>

      </div><!-- end app-right -->

      <!-- Full-screen views (outside main, inside app-layout) -->
            <div class="flex-1 flex flex-col overflow-hidden" style="background:var(--surface);" v-if="currentView === 'unreplied'">
                <div style="border-bottom:1px solid var(--border);padding:0.75rem 1rem;display:flex;justify-content:space-between;align-items:center;background:var(--gray-50);gap:0.5rem;">
                    <div class="flex items-center gap-3">
                        <button class="topbar-hamburger" @click="toggleMobileSidebar()" aria-label="開啟選單">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                        </button>
                        <h2 style="font-size:1.25rem;font-weight:800;color:var(--gray-900);letter-spacing:-0.02em;">待跟進郵件</h2>
                        <span class="hidden-mobile" style="font-size:0.875rem;color:var(--gray-500);">過去 14 天寄出但未收到回覆的郵件</span>
                    </div>
                    <button @click="fetchUnrepliedMessages" class="btn btn-ghost btn-sm" title="重新整理">🔄</button>
                </div>

                <div class="flex-1 overflow-y-auto p-6" style="background:var(--gray-50);">
                    <div v-if="unrepliedLoading" class="empty-state h-64">
                        <span class="spinner"></span><p>正在掃描未回覆郵件...</p>
                    </div>
                    <div v-else-if="unrepliedMessages.length === 0" class="empty-state h-64">
                        <span style="font-size:2.5rem;">🎉</span>
                        <p style="font-size:1.125rem;">太棒了！沒有需要跟進的郵件。</p>
                    </div>
                    <div v-else class="space-y-4 max-w-4xl mx-auto">
                        <div v-for="msg in unrepliedMessages" :key="msg.message_id"
                            class="card flex justify-between items-center" style="padding:1rem;">
                            <div class="flex-1 min-w-0 pr-4">
                                <div class="flex items-center gap-2 mb-1">
                                    <span style="font-weight:700;color:var(--gray-800);" class="truncate">{{ msg.subject || '(無主旨)' }}</span>
                                    <span class="badge badge-warning">未回覆</span>
                                </div>
                                <div style="font-size:0.875rem;color:var(--gray-500);" class="flex items-center gap-4">
                                    <span>收件人: <span style="font-weight:500;color:var(--gray-700);">{{ msg.to }}</span></span>
                                    <span>寄出時間: {{ msg.date }}</span>
                                </div>
                            </div>
                            <button @click="followUpUnreplied(msg.id)" class="btn btn-warning btn-sm">
                                <span>🏃</span> 立即跟進
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- AI Sales View -->
            <div class="flex-1 flex flex-col overflow-hidden" style="background:var(--surface);" v-if="currentView === 'ai_sales'">
                <div style="border-bottom:1px solid var(--border);padding:0.75rem 1rem;display:flex;justify-content:space-between;align-items:center;background:var(--gray-50);gap:0.5rem;">
                    <div class="flex items-center gap-3">
                        <button class="topbar-hamburger" @click="toggleMobileSidebar()" aria-label="開啟選單">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                        </button>
                        <h2 style="font-size:1.25rem;font-weight:800;color:var(--gray-900);letter-spacing:-0.02em;">AI 業務助手</h2>
                        <span class="hidden-mobile" style="font-size:0.875rem;color:var(--gray-500);">待跟進 + AI 回覆草稿</span>
                    </div>
                    <div class="flex gap-2">
                        <button @click="openNotificationsModal" class="btn btn-ghost btn-sm" title="通知中心">🔔</button>
                        <button @click="refreshAiSales" class="btn btn-ghost btn-sm" title="重新整理">🔄</button>
                        <button @click="scanInboundReplies" :disabled="autoRepliesScanLoading"
                            class="btn btn-ghost btn-sm"
                            :title="autoRepliesScanLoading ? '掃描中...' : '掃描收件匣（產生 AI 草稿）'">
                            <span v-if="autoRepliesScanLoading" class="animate-spin inline-block">⟳</span>
                            <span v-else>📥</span>
                        </button>
                        <button @click="scanOpportunities" :disabled="opportunitiesScanLoading"
                            class="btn btn-ghost btn-sm"
                            :title="opportunitiesScanLoading ? '掃描中...' : '掃描收件匣（抽取商機）'">
                            <span v-if="opportunitiesScanLoading" class="animate-spin inline-block">⟳</span>
                            <span v-else>💼</span>
                        </button>
                    </div>
                </div>

                <div class="border-b px-6 py-2 flex gap-2" style="background:var(--surface);">
                    <button @click="aiSalesTab = 'followups'"
                        :class="aiSalesTab === 'followups' ? 'active' : ''"
                        class="tab-item">
                        待跟進
                    </button>
                    <button @click="aiSalesTab = 'opportunities'; fetchOpportunities()"
                        :class="aiSalesTab === 'opportunities' ? 'active' : ''"
                        class="tab-item">
                        商機清單
                    </button>
                    <button @click="aiSalesTab = 'auto_replies'; fetchAutoReplies()"
                        :class="aiSalesTab === 'auto_replies' ? 'active' : ''"
                        class="tab-item">
                        AI 回覆草稿
                    </button>
                    <button @click="aiSalesTab = 'assets'; fetchResources()"
                        :class="aiSalesTab === 'assets' ? 'active' : ''"
                        class="tab-item">
                        素材庫
                    </button>
                    <button @click="aiSalesTab = 'calendar'; fetchCalendarEvents()"
                        :class="aiSalesTab === 'calendar' ? 'active' : ''"
                        class="tab-item">
                        行事曆
                    </button>
                </div>

                <div class="flex-1 overflow-hidden" style="background:var(--gray-50);">
                    <div v-if="aiSalesTab === 'followups'" class="h-full overflow-y-auto p-6">
                        <div class="max-w-4xl mx-auto mb-6">
                            <div class="card p-4">
                                <div class="flex flex-wrap gap-3 items-end">
                                    <div class="flex flex-col">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">層級</label>
                                        <select v-model="followupSequenceForm.level"
                                            class="input w-36">
                                            <option value="hot">熱（幾小時內）</option>
                                            <option value="warm">暖（7–14 天）</option>
                                            <option value="cold">冷（每月保溫）</option>
                                        </select>
                                    </div>
                                    <div class="flex flex-col flex-1 min-w-[220px]">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">收件者 Email</label>
                                        <input v-model="followupSequenceForm.recipient_email"
                                            class="input" placeholder="name@company.com" />
                                    </div>
                                    <div class="flex flex-col flex-1 min-w-[160px]">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">稱呼</label>
                                        <input v-model="followupSequenceForm.recipient_name"
                                            class="input" placeholder="王先生 / 小明" />
                                    </div>
                                    <div class="flex flex-col flex-1 min-w-[220px]">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">主旨線索</label>
                                        <input v-model="followupSequenceForm.context_subject"
                                            class="input" placeholder="產品報價詢問 / 方案比較" />
                                    </div>
                                    <div class="flex flex-col flex-1 min-w-[260px]">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">前情摘要</label>
                                        <input v-model="followupSequenceForm.context_summary"
                                            class="input" placeholder="客戶關心功能、時程、折扣…" />
                                    </div>
                                    <div class="flex flex-col flex-1 min-w-[220px]">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">報價摘要</label>
                                        <input v-model="followupSequenceForm.quote_summary"
                                            class="input" placeholder="Pro 年繳 20 席 / 估算…" />
                                    </div>
                                    <button @click="previewFollowupSequence"
                                        :disabled="followupSequenceLoading"
                                        class="btn btn-primary ml-auto">
                                        <span v-if="followupSequenceLoading" class="animate-spin inline-block mr-2">⟳</span>
                                        預覽序列
                                    </button>
                                </div>

                                <div v-if="followupSequencePreview && followupSequencePreview.steps" class="mt-4 space-y-3">
                                    <div class="text-sm font-bold" style="color:var(--gray-700);">郵件草稿（點擊套用到寄信）</div>
                                    <div v-for="s in followupSequencePreview.steps" :key="s.step"
                                        class="input -lg p-3">
                                        <div class="flex items-center justify-between gap-3">
                                            <div class="min-w-0">
                                                <div class="text-xs" style="color:var(--gray-500);">第 {{ s.step }} 封 · {{ s.send_after_hours }} 小時後</div>
                                                <div class="font-bold truncate" style="color:var(--gray-800);" :title="s.subject">{{ s.subject }}</div>
                                            </div>
                                            <button @click="applyDraftToCompose(followupSequenceForm.recipient_email, s.subject, s.body)"
                                                class="btn btn-success btn-sm">
                                                ✉️ 套用
                                            </button>
                                        </div>
                                        <pre class="mt-2 text-xs whitespace-pre-wrap border rounded p-2" style="color:var(--gray-700);background:var(--gray-50);">{{ s.body }}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="unrepliedLoading" class="empty-state h-64">
                            <span class="spinner"></span><p>正在掃描未回覆郵件...</p>
                        </div>
                        <div v-else-if="unrepliedMessages.length === 0" class="empty-state h-64">
                            <span style="font-size:2.5rem;">🎉</span>
                            <p style="font-size:1.125rem;">太棒了！沒有需要跟進的郵件。</p>
                        </div>
                        <div v-else class="space-y-4 max-w-4xl mx-auto">
                            <div v-for="msg in unrepliedMessages" :key="msg.message_id"
                                class="card flex justify-between items-center" style="padding:1rem;">
                                <div class="flex-1 min-w-0 pr-4">
                                    <div class="flex items-center gap-2 mb-1">
                                        <span style="font-weight:700;color:var(--gray-800);" class="truncate">{{ msg.subject || '(無主旨)' }}</span>
                                        <span class="badge badge-warning">未回覆</span>
                                    </div>
                                    <div style="font-size:0.875rem;color:var(--gray-500);" class="flex items-center gap-4">
                                        <span>收件人: <span style="font-weight:500;color:var(--gray-700);">{{ msg.to }}</span></span>
                                        <span>寄出時間: {{ msg.date }}</span>
                                    </div>
                                </div>
                                <button @click="followUpUnreplied(msg.id)" class="btn btn-warning btn-sm">
                                    <span>🏃</span> 立即跟進
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="aiSalesTab === 'opportunities'" class="h-full overflow-y-auto p-6">
                        <div class="max-w-6xl mx-auto space-y-4">
                            <div class="card p-4 flex flex-wrap gap-3 items-end">
                                <div class="flex flex-col">
                                    <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">搜尋</label>
                                    <input v-model="opportunitiesSearch" @keyup.enter="resetOpportunitiesPaging(); fetchOpportunities()"
                                        class="input w-72" placeholder="公司 / 主旨 / 寄件者 / 需求 / 下一步" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">狀態</label>
                                    <select v-model="opportunitiesStatus" @change="resetOpportunitiesPaging(); fetchOpportunities()"
                                        class="input w-40">
                                        <option value="">全部</option>
                                        <option value="inbound">進線</option>
                                        <option value="qualified">已確認</option>
                                        <option value="proposal">提案/報價</option>
                                        <option value="negotiation">議價/簽約</option>
                                        <option value="won">成交</option>
                                        <option value="lost">流失</option>
                                    </select>
                                </div>
                                <div class="flex flex-col">
                                    <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">最小預算</label>
                                    <input v-model.number="opportunitiesMinBudget" type="number" min="0"
                                        class="input w-40" placeholder="budget_min ≥" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">最大預算</label>
                                    <input v-model.number="opportunitiesMaxBudget" type="number" min="0"
                                        class="input w-40" placeholder="budget_max ≤" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">每頁</label>
                                    <select v-model.number="opportunitiesLimit" @change="resetOpportunitiesPaging(); fetchOpportunities()"
                                        class="input w-28">
                                        <option :value="20">20</option>
                                        <option :value="50">50</option>
                                        <option :value="100">100</option>
                                        <option :value="200">200</option>
                                    </select>
                                </div>
                                <div class="flex flex-col">
                                    <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">排序</label>
                                    <div class="flex gap-2">
                                        <select v-model="opportunitiesSortBy" class="input">
                                            <option value="created_at">建立時間</option>
                                            <option value="budget_min">預算(低)</option>
                                            <option value="timeline_start">時程(起)</option>
                                            <option value="confidence">信心值</option>
                                            <option value="company">公司</option>
                                        </select>
                                        <select v-model="opportunitiesSortDir" class="input">
                                            <option value="desc">由新到舊 / 由大到小</option>
                                            <option value="asc">由舊到新 / 由小到大</option>
                                        </select>
                                    </div>
                                </div>
                                <button @click="resetOpportunitiesPaging(); fetchOpportunities()"
                                    class="btn btn-success ml-auto">
                                    重新載入
                                </button>
                            </div>

                            <div class="card p-4">
                                <div class="flex flex-wrap items-center gap-3">
                                    <div class="text-sm font-bold" style="color:var(--gray-800);">潛客評分（權重可調）</div>
                                    <div class="text-xs" style="color:var(--gray-500);">拖動滑桿即時重新排名</div>
                                    <div class="ml-auto flex items-center gap-2">
                                        <button @click="loadLeadScoring"
                                            :disabled="leadFeaturesLoading || leadScoreWeightsLoading"
                                            class="btn btn-dark btn-sm">
                                            <span v-if="leadFeaturesLoading || leadScoreWeightsLoading" class="animate-spin inline-block mr-2">⟳</span>
                                            載入
                                        </button>
                                        <span v-if="leadScoreWeightsSaving" class="text-xs" style="color:var(--gray-500);">儲存中…</span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4">
                                    <div class="card p-3">
                                        <div class="text-xs font-bold" style="color:var(--gray-600);">意圖</div>
                                        <div class="text-xs mb-2" style="color:var(--gray-500);">互動分數/需求明確</div>
                                        <input type="range" min="0" max="100" step="1"
                                            :value="Math.round((leadScoreWeights.intent || 0) * 100)"
                                            @input="setLeadWeight('intent', $event.target.value)"
                                            class="w-full" />
                                        <div class="text-xs mt-1" style="color:var(--gray-600);">{{ Math.round((normalizedLeadWeights.intent || 0) * 100) }}%</div>
                                    </div>
                                    <div class="card p-3">
                                        <div class="text-xs font-bold" style="color:var(--gray-600);">互動</div>
                                        <div class="text-xs mb-2" style="color:var(--gray-500);">開信/點擊</div>
                                        <input type="range" min="0" max="100" step="1"
                                            :value="Math.round((leadScoreWeights.engagement || 0) * 100)"
                                            @input="setLeadWeight('engagement', $event.target.value)"
                                            class="w-full" />
                                        <div class="text-xs mt-1" style="color:var(--gray-600);">{{ Math.round((normalizedLeadWeights.engagement || 0) * 100) }}%</div>
                                    </div>
                                    <div class="card p-3">
                                        <div class="text-xs font-bold" style="color:var(--gray-600);">新鮮度</div>
                                        <div class="text-xs mb-2" style="color:var(--gray-500);">最近互動</div>
                                        <input type="range" min="0" max="100" step="1"
                                            :value="Math.round((leadScoreWeights.recency || 0) * 100)"
                                            @input="setLeadWeight('recency', $event.target.value)"
                                            class="w-full" />
                                        <div class="text-xs mt-1" style="color:var(--gray-600);">{{ Math.round((normalizedLeadWeights.recency || 0) * 100) }}%</div>
                                    </div>
                                    <div class="card p-3">
                                        <div class="text-xs font-bold" style="color:var(--gray-600);">價值</div>
                                        <div class="text-xs mb-2" style="color:var(--gray-500);">預算/金額</div>
                                        <input type="range" min="0" max="100" step="1"
                                            :value="Math.round((leadScoreWeights.value || 0) * 100)"
                                            @input="setLeadWeight('value', $event.target.value)"
                                            class="w-full" />
                                        <div class="text-xs mt-1" style="color:var(--gray-600);">{{ Math.round((normalizedLeadWeights.value || 0) * 100) }}%</div>
                                    </div>
                                    <div class="card p-3">
                                        <div class="text-xs font-bold" style="color:var(--gray-600);">契合</div>
                                        <div class="text-xs mb-2" style="color:var(--gray-500);">標籤/資料完整</div>
                                        <input type="range" min="0" max="100" step="1"
                                            :value="Math.round((leadScoreWeights.fit || 0) * 100)"
                                            @input="setLeadWeight('fit', $event.target.value)"
                                            class="w-full" />
                                        <div class="text-xs mt-1" style="color:var(--gray-600);">{{ Math.round((normalizedLeadWeights.fit || 0) * 100) }}%</div>
                                    </div>
                                </div>

                                <div v-if="leadFeaturesLoading" class="mt-4 text-center" style="color:var(--gray-500);">
                                    <span class="animate-spin mr-2">⟳</span> 載入潛客資料中...
                                </div>
                                <div v-else-if="rankedLeadFeatures.length === 0" class="mt-4 text-center" style="color:var(--gray-500);">
                                    尚無潛客資料（可先到「聯絡人」建立/匯入，或等待追蹤/商機事件累積）
                                </div>
                                <div v-else class="mt-4 overflow-x-auto">
                                    <table class="min-w-full text-sm">
                                        <thead style="background:var(--gray-50);border:1px solid var(--gray-200);">
                                            <tr class="text-left" style="color:var(--gray-600);">
                                                <th class="px-3 py-2 font-bold">排名</th>
                                                <th class="px-3 py-2 font-bold">總分</th>
                                                <th class="px-3 py-2 font-bold">姓名</th>
                                                <th class="px-3 py-2 font-bold">Email</th>
                                                <th class="px-3 py-2 font-bold">意圖</th>
                                                <th class="px-3 py-2 font-bold">互動</th>
                                                <th class="px-3 py-2 font-bold">新鮮度</th>
                                                <th class="px-3 py-2 font-bold">價值</th>
                                                <th class="px-3 py-2 font-bold">契合</th>
                                                <th class="px-3 py-2 font-bold">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y border">
                                            <template v-for="(l, idx) in rankedLeadFeatures.slice(0, 50)" :key="l.contact_id">
                                                <tr class="">
                                                    <td class="px-3 py-2" style="color:var(--gray-500);">{{ idx + 1 }}</td>
                                                    <td class="px-3 py-2 font-bold" style="color:var(--gray-900);">{{ Math.round(l.total_score) }}</td>
                                                    <td class="px-3 py-2 font-medium" style="color:var(--gray-800);">{{ l.name || '—' }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ l.email || '—' }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ l.dims?.intent ?? 0 }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ l.dims?.engagement ?? 0 }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ l.dims?.recency ?? 0 }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ l.dims?.value ?? 0 }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ l.dims?.fit ?? 0 }}</td>
                                                    <td class="px-3 py-2 whitespace-nowrap">
                                                        <button type="button" @click.stop.prevent="toggleLeadFactors(l.contact_id)"
                                                            class="btn btn-secondary btn-xs mr-2">
                                                            🔎 因子
                                                        </button>
                                                        <button @click="openCrmTimeline(l)"
                                                            class="btn btn-primary btn-xs">
                                                            🧾 CRM
                                                        </button>
                                                    </td>
                                                </tr>
                                                <tr v-if="leadFactorsExpanded[l.contact_id]" style="background:var(--surface);">
                                                    <td colspan="10" class="px-3 py-3">
                                                        <div class="grid grid-cols-1 md:grid-cols-5 gap-3">
                                                            <div class="card p-2">
                                                                <div class="text-xs font-bold" style="color:var(--gray-600);">意圖</div>
                                                                <div class="text-xs mt-1" style="color:var(--gray-700);" v-for="x in (l.factors?.intent || [])" :key="x">{{ x }}</div>
                                                            </div>
                                                            <div class="card p-2">
                                                                <div class="text-xs font-bold" style="color:var(--gray-600);">互動</div>
                                                                <div class="text-xs mt-1" style="color:var(--gray-700);" v-for="x in (l.factors?.engagement || [])" :key="x">{{ x }}</div>
                                                            </div>
                                                            <div class="card p-2">
                                                                <div class="text-xs font-bold" style="color:var(--gray-600);">新鮮度</div>
                                                                <div class="text-xs mt-1" style="color:var(--gray-700);" v-for="x in (l.factors?.recency || [])" :key="x">{{ x }}</div>
                                                            </div>
                                                            <div class="card p-2">
                                                                <div class="text-xs font-bold" style="color:var(--gray-600);">價值</div>
                                                                <div class="text-xs mt-1" style="color:var(--gray-700);" v-for="x in (l.factors?.value || [])" :key="x">{{ x }}</div>
                                                            </div>
                                                            <div class="card p-2">
                                                                <div class="text-xs font-bold" style="color:var(--gray-600);">契合</div>
                                                                <div class="text-xs mt-1" style="color:var(--gray-700);" v-for="x in (l.factors?.fit || [])" :key="x">{{ x }}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="card p-4">
                                <div class="flex flex-wrap items-center gap-3">
                                    <div class="text-sm font-bold" style="color:var(--gray-800);">成交預測（依管道階段）</div>
                                    <div class="text-xs" style="color:var(--gray-500);">顯示平均機率、預期金額與影響因子</div>
                                    <div class="ml-auto flex items-center gap-2">
                                        <label class="text-xs font-bold" style="color:var(--gray-600);">天數</label>
                                        <select v-model.number="pipelineForecastDays" class="input text-xs">
                                            <option :value="14">14</option>
                                            <option :value="30">30</option>
                                            <option :value="60">60</option>
                                            <option :value="90">90</option>
                                        </select>
                                        <button @click="fetchPipelineForecast"
                                            :disabled="pipelineForecastLoading"
                                            class="btn btn-dark btn-sm">
                                            <span v-if="pipelineForecastLoading" class="animate-spin inline-block mr-2">⟳</span>
                                            載入
                                        </button>
                                    </div>
                                </div>

                                <div v-if="pipelineForecastLoading" class="mt-4 text-center" style="color:var(--gray-500);">
                                    <span class="animate-spin mr-2">⟳</span> 載入預測中...
                                </div>
                                <div v-else-if="!pipelineForecast || !pipelineForecast.stages" class="mt-4 text-center" style="color:var(--gray-500);">
                                    尚無預測資料（可先用「掃描收件匣（抽取商機）」產生商機）
                                </div>
                                <div v-else class="mt-4 space-y-3">
                                    <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                                        <div v-for="(v, k) in pipelineForecast.stages" :key="k" class="input p-3">
                                            <div class="text-xs font-bold" style="color:var(--gray-600);">{{ stageLabel(k) }}</div>
                                            <div class="text-sm font-bold mt-1" style="color:var(--gray-900);">{{ v.count }} 筆</div>
                                            <div class="text-xs mt-1" style="color:var(--gray-700);">平均機率：{{ Math.round((v.avg_probability || 0) * 100) }}%</div>
                                            <div class="text-xs" style="color:var(--gray-700);">預期金額：{{ Math.round(v.expected_amount || 0) }}</div>
                                        </div>
                                    </div>
                                    <div class="overflow-x-auto border rounded">
                                        <table class="min-w-full text-sm">
                                            <thead style="background:var(--gray-50);border-bottom:1px solid var(--gray-200);">
                                                <tr class="text-left" style="color:var(--gray-600);">
                                                    <th class="px-3 py-2 font-bold">階段</th>
                                                    <th class="px-3 py-2 font-bold">公司</th>
                                                    <th class="px-3 py-2 font-bold">機率</th>
                                                    <th class="px-3 py-2 font-bold">金額</th>
                                                    <th class="px-3 py-2 font-bold">預期</th>
                                                    <th class="px-3 py-2 font-bold">影響因子</th>
                                                </tr>
                                            </thead>
                                            <tbody class="divide-y">
                                                <tr v-for="(x, idx) in safeArr(flattenedForecastItems).slice(0, 30)" :key="keyById(x, 'forecast', idx)" class="">
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ stageLabel(x.stage) }}</td>
                                                    <td class="px-3 py-2 font-medium" style="color:var(--gray-800);">{{ x.company || '—' }}</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ Math.round((x.probability || 0) * 100) }}%</td>
                                                    <td class="px-3 py-2" style="color:var(--gray-700);">{{ Math.round(x.amount || 0) }}</td>
                                                    <td class="px-3 py-2 font-bold" style="color:var(--gray-900);">{{ Math.round(x.expected_amount || 0) }}</td>
                                                    <td class="px-3 py-2 text-xs" style="color:var(--gray-700);">
                                                        <div v-for="f in (x.factors || [])" :key="f">{{ f }}</div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div v-if="opportunitiesLoading" class="card p-10 text-center" style="color:var(--gray-500);">
                                <span class="animate-spin mr-2">⟳</span> 載入商機中...
                            </div>
                            <div v-else-if="opportunities.length === 0" class="card p-10 text-center" style="color:var(--gray-500);">
                                目前沒有商機資料
                            </div>
                            <div v-else class="card overflow-hidden">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-sm">
                                        <thead style="background:var(--gray-50);border-bottom:1px solid var(--gray-200);">
                                            <tr class="text-left" style="color:var(--gray-600);">
                                                <th class="px-4 py-3 font-bold">時間</th>
                                                <th class="px-4 py-3 font-bold">狀態</th>
                                                <th class="px-4 py-3 font-bold">公司</th>
                                                <th class="px-4 py-3 font-bold">寄件者</th>
                                                <th class="px-4 py-3 font-bold">預算</th>
                                                <th class="px-4 py-3 font-bold">時程</th>
                                                <th class="px-4 py-3 font-bold">下一步</th>
                                                <th class="px-4 py-3 font-bold sticky right-0 border-l z-20" style="background:var(--gray-50);">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y">
                                            <tr v-for="(o, idx) in safeArr(opportunities)" :key="keyById(o, 'opp', idx)" class="">
                                                <td class="px-4 py-3 whitespace-nowrap" style="color:var(--gray-500);">{{ new Date(o.created_at).toLocaleString() }}</td>
                                                <td class="px-4 py-3 whitespace-nowrap font-bold" style="color:var(--gray-700);">{{ stageLabel(o.status || 'inbound') }}</td>
                                                <td class="px-4 py-3 font-bold whitespace-nowrap" style="color:var(--gray-800);">{{ o.company || '—' }}</td>
                                                <td class="px-4 py-3" style="color:var(--gray-700);">
                                                    <div class="font-medium">{{ o.sender_name || '—' }}</div>
                                                    <div class="text-xs" style="color:var(--gray-500);">{{ o.sender_email || '—' }}</div>
                                                </td>
                                                <td class="px-4 py-3" style="color:var(--gray-700);">
                                                    <div class="font-medium">{{ o.budget_text || '—' }}</div>
                                                    <div class="text-xs" style="color:var(--gray-500);" v-if="o.budget_min != null || o.budget_max != null">
                                                        {{ o.budget_min != null ? o.budget_min : '—' }} ~ {{ o.budget_max != null ? o.budget_max : '—' }} {{ o.currency || '' }}
                                                    </div>
                                                </td>
                                                <td class="px-4 py-3" style="color:var(--gray-700);">
                                                    <div class="font-medium">{{ o.timeline || '—' }}</div>
                                                    <div class="text-xs" style="color:var(--gray-500);" v-if="o.timeline_start || o.timeline_end">
                                                        {{ o.timeline_start || '—' }} ~ {{ o.timeline_end || '—' }}
                                                    </div>
                                                </td>
                                                <td class="px-4 py-3 max-w-sm truncate" style="color:var(--gray-700);" :title="o.next_step || ''">{{ o.next_step || '—' }}</td>
                                                <td class="px-4 py-3 whitespace-nowrap sticky right-0 border-l z-10" style="background:var(--surface);">
                                                    <button type="button" @click.stop.prevent="openOpportunityDetailById(o.id)"
                                                        class="btn btn-outline btn-xs mr-2">
                                                        🔎 詳情
                                                    </button>
                                                    <button @click="followUpOpportunity(o)"
                                                        :disabled="!(o.message_id || (o.sender_email && o.subject) || o.imap_id)"
                                                        :title="(o.message_id || (o.sender_email && o.subject) || o.imap_id) ? '建立跟進郵件' : '此商機沒有原信資訊（可能是舊資料）'"
                                                        class="btn btn-success btn-sm mr-2">
                                                        ✉️ 跟進
                                                    </button>
                                                    <button @click="openOpportunityOriginal(o)"
                                                        :disabled="!(o.message_id || (o.sender_email && o.subject) || o.imap_id)"
                                                        :title="(o.message_id || (o.sender_email && o.subject) || o.imap_id) ? '查看原信' : '此商機沒有原信資訊（可能是舊資料）'"
                                                        class="btn btn-secondary btn-sm">
                                                        📨 原信
                                                    </button>
                                                    <button @click="reextractOpportunity(o)"
                                                        :disabled="opportunitiesReextractingId === o.id || !(o.message_id || (o.sender_email && o.subject) || o.imap_id)"
                                                        :title="(o.message_id || (o.sender_email && o.subject) || o.imap_id) ? '重新抽取商機' : '此商機沒有原信資訊（可能是舊資料）'"
                                                        class="btn btn-dark btn-sm ml-2">
                                                        <span v-if="opportunitiesReextractingId === o.id" class="animate-spin inline-block">⟳</span>
                                                        <span v-else>🔄</span>
                                                        重新抽取
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="p-3 text-xs border-t flex justify-between" style="color:var(--gray-500);">
                                    <div>
                                        共 {{ opportunitiesTotal }} 筆
                                        <span v-if="opportunitiesTotal > 0">
                                            · 顯示 {{ Math.min(opportunitiesOffset + 1, opportunitiesTotal) }}-{{ Math.min(opportunitiesOffset + opportunities.length, opportunitiesTotal) }}
                                        </span>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <button @click="goOpportunitiesPrevPage" :disabled="opportunitiesOffset <= 0 || opportunitiesLoading"
                                            class="btn btn-secondary btn-xs">
                                            上一頁
                                        </button>
                                        <button @click="goOpportunitiesNextPage"
                                            :disabled="(opportunitiesOffset + (Number(opportunitiesLimit) || 100)) >= opportunitiesTotal || opportunitiesLoading"
                                            class="btn btn-secondary btn-xs">
                                            下一頁
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="aiSalesTab === 'calendar'" class="h-full overflow-y-auto p-6">
                        <div class="max-w-5xl mx-auto space-y-4">
                            <div class="card p-4">
                                <div class="flex flex-wrap items-center gap-3">
                                    <div class="text-sm font-bold" style="color:var(--gray-800);">行事曆（未來 {{ calendarRangeDays }} 天）</div>
                                    <div class="ml-auto flex items-center gap-2">
                                        <input type="number" min="1" max="90" v-model.number="calendarRangeDays"
                                            class="input w-24" />
                                        <button @click="fetchCalendarEvents" :disabled="calendarLoading"
                                            class="btn btn-dark">
                                            重新載入
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="card p-4">
                                <div class="text-sm font-bold mb-3" style="color:var(--gray-800);">新增事件</div>
                                <div class="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                                    <div class="md:col-span-2">
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">標題</label>
                                        <input v-model="calendarCreateForm.title" class="input" placeholder="例如：產品 Demo" />
                                    </div>
                                    <div>
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">開始</label>
                                        <input v-model="calendarCreateForm.start_time" ref="calendarCreateStartInput" type="datetime-local" step="60"
                                            class="input" />
                                    </div>
                                    <div>
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">結束（可選）</label>
                                        <input v-model="calendarCreateForm.end_time" type="datetime-local" step="60"
                                            class="input" />
                                    </div>
                                    <div class="md:col-span-2">
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">聯絡人 Email（可選）</label>
                                        <input v-model="calendarCreateForm.contact_email" class="input" placeholder="name@company.com" />
                                    </div>
                                    <div class="md:col-span-2">
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">地點（可選）</label>
                                        <input v-model="calendarCreateForm.location" class="input" placeholder="Google Meet / 會議室" />
                                    </div>
                                    <div class="md:col-span-4">
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">描述（可選）</label>
                                        <input v-model="calendarCreateForm.description" class="input" placeholder="議程 / 連結 / 備註" />
                                    </div>
                            <div class="md:col-span-6">
                                <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">AI 解析事件（可選）</label>
                                <textarea v-model="calendarAiText" rows="2" class="input"
                                    placeholder="貼上會議描述，例如：下週三 10:00 與王小明產品 Demo，地點 Google Meet，約 1 小時"></textarea>
                                <div class="mt-2 flex items-center gap-2">
                                    <button @click="parseCalendarAiText" :disabled="calendarAiParsing || !(calendarAiText || '').toString().trim()"
                                        class="btn btn-outline btn-sm">
                                        <span v-if="calendarAiParsing" class="animate-spin inline-block mr-1">⟳</span>
                                        AI 解析
                                    </button>
                                    <button @click="parseCalendarAiTextAndApply" :disabled="calendarAiParsing || !(calendarAiText || '').toString().trim()"
                                        class="btn btn-dark btn-sm border">
                                        一鍵帶入
                                    </button>
                                    <button @click="calendarAiText = ''" :disabled="calendarAiParsing"
                                        class="btn btn-outline btn-sm">
                                        清除
                                    </button>
                                    <span class="text-xs" style="color:var(--gray-500);">解析後可直接「帶入到新增事件」或「建立行事曆」</span>
                                </div>
                            </div>
                            <div class="md:col-span-6 flex items-center gap-2">
                                <button @click="suggestCalendarTimes" :disabled="calendarCreating || calendarSuggesting"
                                    class="btn btn-outline btn-sm">
                                    <span v-if="calendarSuggesting" class="animate-spin inline-block mr-1">⟳</span>
                                    AI 建議時段
                                </button>
                                <span class="text-xs" style="color:var(--gray-500);" v-if="calendarSuggestedSlots && calendarSuggestedSlots.length">點選一個建議以帶入</span>
                            </div>
                            <div v-if="calendarSuggestedSlots && calendarSuggestedSlots.length" class="md:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                                <button v-for="s in calendarSuggestedSlots" :key="s.start_at"
                                    @click="applySuggestedSlot(s)"
                                    class="text-left px-3 py-2 rounded border text-xs"
                                    :style="(calendarSelectedSlotStartAt && calendarSelectedSlotStartAt === s.start_at) ? 'background:var(--brand-50);border-color:var(--brand-500);' : ''">
                                    <div class="font-semibold" style="color:var(--gray-800);">{{ formatCalendarTime(s.start_at) }} → {{ formatCalendarTime(s.end_at) }}</div>
                                    <div style="color:var(--gray-500);">{{ s.day }}</div>
                                </button>
                            </div>
                                    <button @click="createCalendarEventManual" :disabled="calendarCreating"
                                        class="btn btn-success md:col-span-6">
                                        {{ calendarCreating ? '建立中...' : '建立事件' }}
                                    </button>
                                </div>
                            </div>

                            <div class="card overflow-hidden">
                                <div class="p-4 border-b flex items-center justify-between gap-3">
                                    <div class="text-sm font-bold" style="color:var(--gray-800);">事件清單</div>
                                    <div class="text-xs" style="color:var(--gray-500);">共 {{ calendarTotal }} 筆</div>
                                </div>
                                <div v-if="calendarLoading" class="p-6 flex items-center justify-center" style="color:var(--gray-500);">
                                    <span class="animate-spin mr-2">⟳</span> 載入中...
                                </div>
                                <div v-else class="overflow-x-auto">
                                    <table class="data-table">
                                        <thead style="background:var(--gray-50);color:var(--gray-600);">
                                            <tr>
                                                <th class="text-left px-3 py-2 font-bold">標題</th>
                                                <th class="text-left px-3 py-2 font-bold">開始</th>
                                                <th class="text-left px-3 py-2 font-bold">結束</th>
                                                <th class="text-left px-3 py-2 font-bold">聯絡人</th>
                                                <th class="text-left px-3 py-2 font-bold">狀態</th>
                                                <th class="text-right px-3 py-2 font-bold">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(e, idx) in safeArr(calendarEvents)" :key="keyById(e, 'cal', idx)" class="border-t">
                                                <td class="px-3 py-2 font-bold" style="color:var(--gray-800);">{{ e.title }}</td>
                                                <td class="px-3 py-2" style="color:var(--gray-700);">{{ formatCalendarTime(e.start_at) }}</td>
                                                <td class="px-3 py-2" style="color:var(--gray-700);">{{ formatCalendarTime(e.end_at) }}</td>
                                                <td class="px-3 py-2">
                                                    <div class="flex items-center gap-2">
                                                        <span style="color:var(--gray-700);">{{ e.contact_email || (e.contact_id ? ('#' + e.contact_id) : '—') }}</span>
                                                        <button v-if="e.contact_id" @click="openCrmTimeline({ contact_id: e.contact_id })"
                                                            class="btn btn-primary btn-xs">
                                                            CRM
                                                        </button>
                                                    </div>
                                                </td>
                                                <td class="px-3 py-2">
                                                    <span
                                                        class="badge"
                                                        :class="(e.status || '').toLowerCase() === 'canceled' ? 'badge-danger' : ((e.status || '').toLowerCase() === 'draft' ? 'badge-warning' : 'badge-success')">
                                                        {{ e.status || '—' }}
                                                    </span>
                                                </td>
                                                <td class="px-3 py-2">
                                                    <div class="flex justify-end gap-2">
                                                        <button @click="openCalendarEdit(e)"
                                                            class="btn btn-primary btn-sm">
                                                            編輯
                                                        </button>
                                                        <button @click="downloadCalendarIcs(e.id)"
                                                            class="btn btn-secondary btn-sm">
                                                            📅 ICS
                                                        </button>
                                                        <button @click="cancelCalendarEvent(e.id)"
                                                            class="btn btn-warning btn-sm">
                                                            取消
                                                        </button>
                                                        <button @click="deleteCalendarEvent(e.id)"
                                                            class="btn btn-danger btn-sm">
                                                            刪除
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr v-if="calendarEvents.length === 0">
                                                <td colspan="6" class="px-3 py-8 text-center" style="color:var(--gray-400);">目前區間沒有事件</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="aiSalesTab === 'assets'" class="h-full overflow-y-auto p-6">
                        <div class="max-w-6xl mx-auto space-y-4">
                            <div class="card p-4">
                                <div class="flex flex-wrap gap-3 items-end">
                                    <div class="flex flex-col">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">搜尋</label>
                                        <input v-model="resourcesSearch" @keyup.enter="fetchResources()"
                                            class="input w-72" placeholder="檔名 / tag / 描述" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">類型</label>
                                        <select v-model="resourcesTypeFilter" @change="fetchResources()"
                                            class="input w-40">
                                            <option value="">全部</option>
                                            <option value="image">image</option>
                                            <option value="video">video</option>
                                            <option value="pdf">pdf</option>
                                            <option value="html">html</option>
                                            <option value="file">file</option>
                                        </select>
                                    </div>
                                    <button @click="fetchResources()" class="btn btn-dark ml-auto">
                                        重新載入
                                    </button>
                                </div>
                            </div>

                            <div class="card p-4">
                                <div class="text-sm font-bold mb-3" style="color:var(--gray-800);">上傳素材</div>
                                <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                    <div class="md:col-span-2">
                                        <input type="file" @change="handleResourceFileSelected"
                                            class="input" />
                                    </div>
                                    <div>
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">可見性</label>
                                        <select v-model="resourceUpload.visibility" class="input">
                                            <option value="private">私有（僅自己）</option>
                                            <option value="shared">分享（指定使用者）</option>
                                            <option v-if="isDomainAdmin || isSuperAdmin" value="domain">網域（全員可見）</option>
                                            <option v-if="isSuperAdmin" value="public">公開（外部可見）</option>
                                        </select>
                                    </div>
                                    <button @click="uploadResource" :disabled="resourcesUploading || !resourceUpload.file"
                                        class="btn btn-success">
                                        {{ resourcesUploading ? '上傳中...' : '上傳' }}
                                    </button>
                                    <div class="md:col-span-2">
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">Tags（逗號分隔）</label>
                                        <input v-model="resourceUpload.tags" class="input" placeholder="dm, 產品簡報" />
                                    </div>
                                    <div class="md:col-span-2">
                                        <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">描述</label>
                                        <input v-model="resourceUpload.description" class="input" placeholder="用途 / 版本 / 備註" />
                                    </div>
                                </div>
                            </div>

                            <div v-if="isDomainAdmin || isSuperAdmin" class="card p-4">
                                <div class="flex items-end gap-3">
                                    <div class="flex-1">
                                        <div class="text-sm font-bold mb-2" style="color:var(--gray-800);">NAS 匯入</div>
                                        <div class="flex gap-2 items-end">
                                            <div class="flex flex-col">
                                                <label class="text-xs font-bold mb-1" style="color:var(--gray-600);">子資料夾</label>
                                                <input v-model="nasSubdir" class="input w-72" placeholder="例如: marketing/2026" />
                                            </div>
                                            <button @click="fetchNasFiles" :disabled="nasLoading"
                                                class="btn btn-secondary">
                                                {{ nasLoading ? '載入中...' : '載入 NAS 清單' }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-3 overflow-x-auto">
                                    <table class="min-w-full text-sm">
                                        <thead style="background:var(--gray-50);border-bottom:1px solid var(--gray-200);">
                                            <tr class="text-left" style="color:var(--gray-600);">
                                                <th class="px-4 py-3 font-bold">路徑</th>
                                                <th class="px-4 py-3 font-bold">大小</th>
                                                <th class="px-4 py-3 font-bold">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y">
                                            <tr v-for="f in nasFiles" :key="f.path" class="">
                                                <td class="px-4 py-3 font-medium" style="color:var(--gray-800);">{{ f.path }}</td>
                                                <td class="px-4 py-3" style="color:var(--gray-600);">{{ formatBytes(f.size) }}</td>
                                                <td class="px-4 py-3 whitespace-nowrap">
                                                    <button @click="importNasFile(f.path)" :disabled="nasImportingPath === f.path"
                                                        class="btn btn-primary btn-sm">
                                                        {{ nasImportingPath === f.path ? '匯入中...' : '匯入' }}
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr v-if="nasFiles.length === 0">
                                                <td colspan="3" class="px-4 py-6 text-center" style="color:var(--gray-500);">沒有檔案</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="card overflow-hidden">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-sm">
                                        <thead style="background:var(--gray-50);border-bottom:1px solid var(--gray-200);">
                                            <tr class="text-left" style="color:var(--gray-600);">
                                                <th class="px-4 py-3 font-bold">時間</th>
                                                <th class="px-4 py-3 font-bold">檔名</th>
                                                <th class="px-4 py-3 font-bold">類型</th>
                                                <th class="px-4 py-3 font-bold">可見性</th>
                                                <th class="px-4 py-3 font-bold">Tags</th>
                                                <th class="px-4 py-3 font-bold sticky right-0 border-l" style="background:var(--gray-50);">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y">
                                            <tr v-for="(r, idx) in safeArr(filteredResources)" :key="keyById(r, 'res', idx)" class="">
                                                <td class="px-4 py-3 whitespace-nowrap" style="color:var(--gray-500);">{{ new Date(r.created_at).toLocaleString() }}</td>
                                                <td class="px-4 py-3 font-medium" style="color:var(--gray-800);">{{ r.original_filename }}</td>
                                                <td class="px-4 py-3" style="color:var(--gray-600);">{{ r.file_type || 'file' }}</td>
                                                <td class="px-4 py-3" style="color:var(--gray-600);">{{ r.visibility || 'domain' }}</td>
                                                <td class="px-4 py-3" style="color:var(--gray-600);">{{ r.tags || '—' }}</td>
                                                <td class="px-4 py-3 whitespace-nowrap sticky right-0 border-l" style="background:var(--surface);">
                                                    <a :href="getResourceUrl(r)" target="_blank"
                                                        class="btn btn-secondary btn-sm mr-2 inline-block">
                                                        下載/開啟
                                                    </a>
                                                    <button v-if="canManageResource(r)" @click="openEditResourceModal(r)"
                                                        class="btn btn-warning btn-sm mr-2">
                                                        編輯
                                                    </button>
                                                    <button v-if="canManageResource(r) && (r.visibility === 'shared')" @click="openShareResourceModal(r)"
                                                        class="btn btn-primary btn-sm mr-2">
                                                        授權
                                                    </button>
                                                    <button v-if="canManageResource(r)" @click="deleteResource(r)"
                                                        class="btn btn-danger btn-sm">
                                                        刪除
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr v-if="resourcesLoading">
                                                <td colspan="6" class="px-4 py-8 text-center" style="color:var(--gray-500);">
                                                    <span class="animate-spin mr-2">⟳</span> 載入中...
                                                </td>
                                            </tr>
                                            <tr v-else-if="filteredResources.length === 0">
                                                <td colspan="6" class="px-4 py-8 text-center" style="color:var(--gray-500);">目前沒有素材</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div v-if="showShareResourceModal" class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,0.4);">
                                <div class="card p-6 -xl w-full max-w-2xl p-6">
                                    <div class="flex items-center justify-between mb-4">
                                        <div class="font-bold" style="color:var(--gray-900);">授權分享</div>
                                        <button @click="closeShareResourceModal" class="font-bold" style="color:var(--gray-500);">✕</button>
                                    </div>
                                    <div class="text-sm mb-3" style="color:var(--gray-600);">
                                        檔案：<span class="font-medium" style="color:var(--gray-800);">{{ shareResourceTarget?.original_filename }}</span>
                                    </div>
                                    <div class="border rounded-lg p-3 max-h-72 overflow-y-auto" style="background:var(--surface);">
                                        <div v-if="usersBriefLoading" class="text-center py-8" style="color:var(--gray-500);">
                                            <span class="animate-spin mr-2">⟳</span> 載入使用者...
                                        </div>
                                        <div v-else-if="safeArr(usersBrief).length === 0" class="text-center py-8" style="color:var(--gray-500);">沒有可授權的使用者</div>
                                        <label v-else v-for="(u, idx) in safeArr(usersBrief)" :key="keyById(u, 'user-brief', idx)" class="flex items-center gap-2 py-1">
                                            <input type="checkbox" class="h-4 w-4" :value="u.id" v-model="shareUserIds">
                                            <span class="text-sm" style="color:var(--gray-800);">{{ u.email }}</span>
                                            <span class="text-xs" style="color:var(--gray-500);">({{ u.role }})</span>
                                        </label>
                                    </div>
                                    <div class="mt-4 flex justify-end gap-2">
                                        <button @click="closeShareResourceModal" class="btn btn-secondary">
                                            取消
                                        </button>
                                        <button @click="saveResourceShares" :disabled="shareSaving"
                                            class="btn btn-primary">
                                            {{ shareSaving ? '儲存中...' : '儲存授權' }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="showEditResourceModal" class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,0.4);">
                                <div class="card p-6 -xl w-full max-w-xl p-6">
                                    <div class="flex items-center justify-between mb-4">
                                        <div class="font-bold" style="color:var(--gray-900);">編輯素材</div>
                                        <button @click="closeEditResourceModal" class="font-bold" style="color:var(--gray-500);">✕</button>
                                    </div>
                                    <div class="text-sm mb-3" style="color:var(--gray-600);">
                                        檔案：<span class="font-medium" style="color:var(--gray-800);">{{ editResourceTarget?.original_filename }}</span>
                                    </div>
                                    <div class="grid grid-cols-1 gap-3">
                                        <div>
                                            <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">可見性</label>
                                            <select v-model="editResourceForm.visibility" class="input">
                                                <option value="private">私有（僅自己）</option>
                                                <option value="shared">分享（指定使用者）</option>
                                                <option v-if="isDomainAdmin || isSuperAdmin" value="domain">網域（全員可見）</option>
                                                <option v-if="isSuperAdmin" value="public">公開（外部可見）</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">Tags（逗號分隔）</label>
                                            <input v-model="editResourceForm.tags" class="input" />
                                        </div>
                                        <div>
                                            <label class="text-xs font-bold mb-1 block" style="color:var(--gray-600);">描述</label>
                                            <input v-model="editResourceForm.description" class="input" />
                                        </div>
                                    </div>
                                    <div class="mt-4 flex justify-end gap-2">
                                        <button @click="closeEditResourceModal" class="btn btn-secondary">
                                            取消
                                        </button>
                                        <button @click="saveEditResource" :disabled="editSaving"
                                            class="btn btn-warning">
                                            {{ editSaving ? '儲存中...' : '儲存' }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="aiSalesTab === 'auto_replies'" class="h-full flex overflow-hidden">
                        <div class="w-1/2 border-r overflow-y-auto" style="background:var(--surface);">
                            <div class="p-4 border-b flex items-center gap-3">
                                <div class="text-sm font-bold" style="color:var(--gray-700);">狀態</div>
                                <select v-model="autoRepliesFilterStatus" @change="fetchAutoReplies()"
                                    class="input">
                                    <option value="">全部</option>
                                    <option value="pending">pending</option>
                                    <option value="approved">approved</option>
                                    <option value="rejected">rejected</option>
                                    <option value="sent">sent</option>
                                </select>
                                <button @click="fetchAutoReplies()"
                                    class="btn btn-secondary btn-sm ml-auto">
                                    重新載入
                                </button>
                            </div>

                            <div v-if="autoRepliesLoading" class="p-6 text-center" style="color:var(--gray-500);">
                                <span class="animate-spin mr-2">⟳</span> 載入 AI 草稿中...
                            </div>
                            <div v-else-if="safeArr(autoReplies).length === 0" class="p-6 text-center" style="color:var(--gray-500);">
                                目前沒有 AI 回覆草稿
                            </div>
                            <div v-else class="divide-y">
                                <button v-for="(r, idx) in safeArr(autoReplies)" :key="keyById(r, 'ar', idx)" @click="selectAutoReply(r)"
                                    class="w-full text-left p-4"
                                    :style="selectedAutoReply?.id === r.id ? 'background:var(--brand-50)' : ''">
                                    <div class="flex items-center justify-between gap-3">
                                        <div class="min-w-0">
                                            <div class="font-bold truncate" style="color:var(--gray-800);">{{ r.ai_draft_subject || r.original_subject || '(無主旨)' }}</div>
                                            <div class="text-xs mt-1" style="color:var(--gray-500);">
                                                {{ formatContactLabel(r.contact_id) }} · {{ new Date(r.created_at).toLocaleString() }}
                                            </div>
                                        </div>
                                        <span class="badge"
                                            :class="r.status === 'pending' ? 'badge-warning' : (r.status === 'approved' ? 'badge-success' : (r.status === 'rejected' ? 'badge-danger' : 'badge-default'))">
                                            {{ r.status }}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <div class="flex-1 overflow-y-auto p-6">
                            <div v-if="!selectedAutoReply" class="h-full flex items-center justify-center" style="color:var(--gray-400);">
                                選擇一筆草稿以檢視與編輯
                            </div>
                            <div v-else class="space-y-4">
                                <div class="flex items-center justify-between gap-3">
                                    <div>
                                        <div class="text-sm" style="color:var(--gray-500);">聯絡人</div>
                                        <div class="font-bold" style="color:var(--gray-800);">{{ formatContactLabel(selectedAutoReply.contact_id) }}</div>
                                    </div>
                                    <div class="flex gap-2">
                                        <button @click="openSelectedAutoReplyOriginal()"
                                            :disabled="!(selectedAutoReply?.original_message_id || selectedAutoReply?.original_imap_id || (contactCache[selectedAutoReply?.contact_id]?.email && selectedAutoReply?.original_subject))"
                                            class="btn btn-secondary"
                                            :title="(selectedAutoReply?.original_message_id || selectedAutoReply?.original_imap_id || (contactCache[selectedAutoReply?.contact_id]?.email && selectedAutoReply?.original_subject)) ? '在「我的信箱」開啟原始來信' : '此草稿沒有原信資訊（可能是舊資料）'">
                                            查看原信
                                        </button>
                                        <button @click="applyAutoReplyToCompose()"
                                            class="btn btn-primary">
                                            帶入撰寫
                                        </button>
                                        <button @click="sendSelectedAutoReply()"
                                            :disabled="autoRepliesLoading"
                                            class="btn btn-dark">
                                            送出
                                        </button>
                                        <button @click="updateAutoReplyStatus('approved')"
                                            class="btn btn-success">
                                            核准
                                        </button>
                                        <button @click="updateAutoReplyStatus('rejected')"
                                            class="btn btn-danger">
                                            拒絕
                                        </button>
                                    </div>
                                </div>

                                <div class="card p-4">
                                    <div class="text-sm font-bold mb-2" style="color:var(--gray-700);">原始來信</div>
                                    <div class="text-sm mb-2" style="color:var(--gray-600);">主旨：{{ selectedAutoReply.original_subject }}</div>
                                    <textarea class="input p-3" rows="6" readonly
                                        :value="selectedAutoReply.original_body"></textarea>
                                </div>

                                <div class="card p-4 space-y-3">
                                    <div class="flex items-center gap-3">
                                        <div class="text-sm font-bold" style="color:var(--gray-700);">草稿狀態</div>
                                        <select v-model="autoReplyEdit.status" class="input">
                                            <option value="pending">pending</option>
                                            <option value="approved">approved</option>
                                            <option value="rejected">rejected</option>
                                            <option value="sent">sent</option>
                                        </select>
                                    </div>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <div class="text-sm font-bold mb-1" style="color:var(--gray-700);">重新生成：語氣</div>
                                            <select v-model="autoReplyRegenerateForm.tone" class="input">
                                                <option value="professional">professional</option>
                                                <option value="friendly">friendly</option>
                                                <option value="concise">concise</option>
                                                <option value="polite">polite</option>
                                            </select>
                                        </div>
                                        <div>
                                            <div class="text-sm font-bold mb-1" style="color:var(--gray-700);">重新生成：模板</div>
                                            <select v-model="autoReplyRegenerateForm.template" class="input">
                                                <option value="standard_reply">standard_reply</option>
                                                <option value="acknowledge_and_confirm">acknowledge_and_confirm</option>
                                                <option value="ask_clarifying">ask_clarifying</option>
                                                <option value="provide_options">provide_options</option>
                                                <option value="polite_decline">polite_decline</option>
                                            </select>
                                        </div>
                                        <div>
                                            <div class="text-sm font-bold mb-1" style="color:var(--gray-700);">重新生成：長度</div>
                                            <select v-model="autoReplyRegenerateForm.length" class="input">
                                                <option value="short">short</option>
                                                <option value="medium">medium</option>
                                                <option value="long">long</option>
                                            </select>
                                        </div>
                                        <div>
                                            <div class="text-sm font-bold mb-1" style="color:var(--gray-700);">重新生成：語言</div>
                                            <select v-model="autoReplyRegenerateForm.language" class="input">
                                                <option value="auto">auto（跟原信同語言）</option>
                                                <option value="Traditional Chinese (繁體中文)">繁體中文</option>
                                                <option value="English">English</option>
                                                <option value="日本語">日本語</option>
                                            </select>
                                        </div>
                                        <div class="flex items-center gap-2 md:col-span-2">
                                            <input id="autoReplyIncludeQuote" type="checkbox" v-model="autoReplyRegenerateForm.include_quote" class="h-4 w-4">
                                            <label for="autoReplyIncludeQuote" class="text-sm font-medium" style="color:var(--gray-700);">附上引用原信（blockquote）</label>
                                            <button @click="regenerateSelectedAutoReply()"
                                                :disabled="autoRepliesLoading"
                                                class="btn btn-primary ml-auto">
                                                重新生成草稿
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <div class="text-sm font-bold mb-1" style="color:var(--gray-700);">草稿主旨</div>
                                        <input v-model="autoReplyEdit.draft_subject" class="input">
                                    </div>
                                    <div>
                                        <div class="text-sm font-bold mb-1" style="color:var(--gray-700);">草稿內容</div>
                                        <textarea v-model="autoReplyEdit.draft_body" class="input p-3" rows="10"></textarea>
                                    </div>
                                    <div class="flex justify-end">
                                        <button @click="saveAutoReplyEdit()"
                                            class="btn btn-dark">
                                            儲存
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <!-- Webmail View -->
            <WebmailView />

    </div><!-- end app-layout -->

    <!-- Global Modals (quarantine, etc.) -->
    <GlobalModals />

    <!-- Toast system -->
    <AppToast />

    <!-- AI 客服小幫手 — 只對一般使用者顯示 -->
    <AiSupportChat v-if="token && currentUser" />
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from './store/app.js'

import AppSidebar from './components/AppSidebar.vue'
import AppTopbar from './components/AppTopbar.vue'
import AppToast from './components/AppToast.vue'
import GlobalModals from './components/GlobalModals.vue'
import AiSupportChat from './components/AiSupportChat.vue'
import SupportStatsView from './views/SupportStatsView.vue'

// Views
import DashboardView from './views/DashboardView.vue'
import RagChatView from './views/RagChatView.vue'
import InvoiceDriveView from './views/InvoiceDriveView.vue'
import DomainsView from './views/DomainsView.vue'
import MailQueueView from './views/MailQueueView.vue'
import LogAnalysisView from './views/LogAnalysisView.vue'
import ServerSslView from './views/ServerSslView.vue'
import SecurityWizardView from './views/SecurityWizardView.vue'
import UsersView from './views/UsersView.vue'
import DepartmentsView from './views/DepartmentsView.vue'
import ContactsView from './views/ContactsView.vue'
import CrmView from './views/CrmView.vue'
import GroupsView from './views/GroupsView.vue'
import ServerMailView from './views/ServerMailView.vue'
import ServerDnsView from './views/ServerDnsView.vue'
import ServerDbView from './views/ServerDbView.vue'
import ServerAiView from './views/ServerAiView.vue'
import BrandingView from './views/BrandingView.vue'
import BackupSettingsView from './views/BackupSettingsView.vue'
import SecurityFilterView from './views/SecurityFilterView.vue'
import MailAuditRecordsView from './views/MailAuditRecordsView.vue'
import SecurityAuditView from './views/SecurityAuditView.vue'
import LogsView from './views/LogsView.vue'
import UnrepliedView from './views/UnrepliedView.vue'
import AiSalesView from './views/AiSalesView.vue'
import WebmailView from './views/WebmailView.vue'
import OutgoingApprovalView from './views/OutgoingApprovalView.vue'
import MarketingReportView from './views/MarketingReportView.vue'
import ProfileView from './views/ProfileView.vue'
import SieveFilterView from './views/SieveFilterView.vue'
import SecurePortalView from './views/SecurePortalView.vue'
import SecureSentView from './views/SecureSentView.vue'
import AlertCenterView from './views/AlertCenterView.vue'
import TenantManagementView from './views/TenantManagementView.vue'
import LandingPageView from './views/LandingPageView.vue'
import SurveyView from './views/SurveyView.vue'
import PrefCenterView from './views/PrefCenterView.vue'
import BookingView from './views/BookingView.vue'
import ReportScheduleView from './views/ReportScheduleView.vue'
import MarketingCalendarView from './views/MarketingCalendarView.vue'

const store = useAppStore()

// Expose all store state + getters used in template
const {
  // Auth & navigation
  token, currentUser, currentView,
  loginForm, loginError,
  loginEmailSuggestions, loginDomainHint,
  showTotpModal, totpPartialToken, totpCode, totpError, totpLoading,
  // Branding
  branding,
  // Command palette
  cmdQuery, cmdResults, showCmdPalette,
  // Calendar
  calendarAiParsing, calendarEvents, calendarLoading,
  calendarSuggestedSlots, calendarSuggesting,
  calendarAiText, calendarCreateForm,
  calendarRangeDays, calendarCreating, calendarTotal,
  // Opportunities / sales
  opportunities, opportunitiesLoading, opportunitiesReextractingId,
  opportunitiesScanLoading, opportunitiesTotal,
  opportunitiesSearch, opportunitiesSortBy, opportunitiesSortDir, opportunitiesStatus,
  pipelineForecast, pipelineForecastLoading,
  followupSequenceLoading, followupSequencePreview, followupSequenceForm,
  leadFactorsExpanded, leadFeaturesLoading, leadScoreWeightsSaving, leadScoreWeights, normalizedLeadWeights,
  // Quick replies & auto reply
  quickRepliesError, quickRepliesLoading, quickRepliesIncludeInactive,
  quickRepliesSearch, quickReplyInsertMode,
  editingQuickReplyId, createQuickReplyForm,
  selectedAutoReply, autoReplies, autoRepliesLoading, autoRepliesScanLoading,
  autoRepliesFilterStatus, autoReplyEdit, autoReplyRegenerateForm,
  // Resources & NAS
  resourcesLoading, resourcesSearch, resourcesTypeFilter, resourceUpload,
  resourcesUploading,
  showEditResourceModal, showShareResourceModal, shareUserIds, editResourceForm,
  nasFiles, nasSubdir, nasLoading, usersBriefLoading,
  // Unreplied
  unrepliedLoading, unrepliedMessages,
  // AI Sales
  aiSalesTab,
  // Getters (computed)
  safeLineConversations, safeLineMessages, safeLineSettings,
  filteredResources, rankedLeadFeatures,
  isSuperAdmin, isDomainAdmin, selectedConversationSetting,
  mobileSidebarOpen,
} = storeToRefs(store)

// Expose store methods used in template
const {
  updateCmdResults, execCmdItemById,
  fetchAutoReplies, selectAutoReply, openSelectedAutoReplyOriginal,
  saveAutoReplyEdit, sendSelectedAutoReply, regenerateSelectedAutoReply,
  updateAutoReplyStatus, startCreateQuickReply, startEditQuickReply,
  saveQuickReply, deleteQuickReply,
  fetchQuickReplies, applyQuickReplyBody,
  fetchLineConversations, selectLineConversation, toggleLineConversationStatus,
  claimLineConversation, unassignLineConversation,
  assignLineConversationDepartment, assignLineConversationUser,
  scrollToLineConversations, scrollToLineSettings,
  selectLineSetting, startCreateLineSetting, saveLineSetting,
  restoreWorkingLineSetting, restoreLineSetting, refreshLineSupport, debugLineConversations,
  getLineWebhookUrl, copyToClipboard,
  sendLineMessage, suggestLineReply, applyLineSuggestion,
  toggleLineAiSource, openLineRagDocPicker, closeLineRagDocPicker,
  fetchKbPickerDocs, loadMoreKbPickerDocs, toggleKbPickerSelected,
  applyKbPickerSelection,
  fetchResources, openEditResourceModal, openShareResourceModal,
  deleteResource, importNasFile,
  openCrmTimeline, followUpOpportunity, reextractOpportunity,
  openOpportunityOriginal, resetOpportunitiesPaging,
  setLeadWeight, followUpUnreplied,
  cancelCalendarEvent, deleteCalendarEvent, openCalendarEdit,
  applySuggestedSlot, downloadCalendarIcs,
  applyDraftToCompose, applyAutoReplyToCompose,
  canManageResource, safeArr, keyById, stageLabel,
  // Auth
  login, logout, totpLogin,
  // Calendar
  createCalendarEventManual, fetchCalendarEvents,
  parseCalendarAiText, parseCalendarAiTextAndApply, suggestCalendarTimes,
  // Opportunities
  scanOpportunities, scanInboundReplies, refreshAiSales,
  goOpportunitiesNextPage, goOpportunitiesPrevPage,
  previewFollowupSequence, loadLeadScoring,
  // Resources
  handleResourceFileSelected, uploadResource,
  saveEditResource, closeEditResourceModal,
  saveResourceShares, closeShareResourceModal,
  fetchNasFiles,
  // Pipeline & misc
  fetchPipelineForecast, fetchUnrepliedMessages,
  openNotificationsModal,
  closeMobileSidebar, toggleMobileSidebar,
} = store

// Map standard views (rendered inside <main>)
const mainViewMap = {
  dashboard: DashboardView,
  rag_chat: RagChatView,
  invoice_drive: InvoiceDriveView,
  domains: DomainsView,
  mail_queue: MailQueueView,
  log_analysis: LogAnalysisView,
  server_ssl: ServerSslView,
  security_wizard: SecurityWizardView,
  users: UsersView,
  departments: DepartmentsView,
  contacts: ContactsView,
  crm: CrmView,
  groups: GroupsView,
  server_mail: ServerMailView,
  server_dns: ServerDnsView,
  server_db: ServerDbView,
  server_ai: ServerAiView,
  branding: BrandingView,
  backup_settings: BackupSettingsView,
  security_filter: SecurityFilterView,
  mail_audit_records: MailAuditRecordsView,
  security_audit: SecurityAuditView,
  logs: LogsView,
  outgoing_approval: OutgoingApprovalView,
  marketing_report: MarketingReportView,
  profile: ProfileView,
  sieve_filter: SieveFilterView,
  secure_portal: SecureSentView,
  alert_center: AlertCenterView,
  tenant_management: TenantManagementView,
  landing_page: LandingPageView,
  survey: SurveyView,
  pref_center: PrefCenterView,
  booking: BookingView,
  report_schedule: ReportScheduleView,
  marketing_calendar: MarketingCalendarView,
  support_stats: SupportStatsView,
}

// Full-screen views are handled directly in template via v-if
// (unreplied, ai_sales, line_support, webmail)

const currentViewComponent = computed(() => mainViewMap[currentView.value] || null)

onMounted(async () => {
  await store.onAppMounted()
})

onBeforeUnmount(async () => {
  await store.onBeforeUnmount()
})

// Apply dark mode on store.darkMode change
watch(() => store.darkMode, (v) => {
  document.documentElement.setAttribute('data-theme', v ? 'dark' : 'light')
}, { immediate: true })

// Set up watch handlers (converted from original Vue options API watch: {})
watch(() => store.currentView, (v, old) => store.onWatch_currentView(v, old))
watch(() => store.invoiceDriveState?.detailModalOpen, (v) => store.onWatch_invoiceDriveState_detailModalOpen(v))
watch(() => store.showComposeModal, (v) => store.onWatch_showComposeModal(v))
watch(() => store.composeForm?.subject, () => store.onWatch_composeForm_subject())
watch(() => store.composeForm?.body, () => store.onWatch_composeForm_body())
watch(() => store.composeForm?.html_body, () => store.onWatch_composeForm_html_body())
watch(() => store.showAiMarketingModal, (v) => store.onWatch_showAiMarketingModal(v))
watch(() => store.aiTab, (v) => store.onWatch_aiTab(v))
</script>
